import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { toast } from 'sonner';
import { ArrowLeft, Send, MessageCircle, User, Clock, MessageSquareOff } from 'lucide-react';
import { motion } from 'framer-motion';

const ChatPage = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const user = getUser();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/conversations');
      setConversations(response.data);
      
      // If conversationId is provided, select it
      if (conversationId) {
        const conv = response.data.find(c => c.id === conversationId);
        if (conv) {
          setSelectedConversation(conv);
        }
      }
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await api.get(`/chat/conversations/${convId}/messages`);
      setMessages(response.data.messages);
      setChatEnabled(response.data.chat_enabled);
      setSelectedConversation(response.data.conversation);
      
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setIsSending(true);
    try {
      await api.post('/chat/send', {
        content: newMessage.trim(),
        course_id: selectedConversation.course_id,
        recipient_id: user.role === 'teacher' 
          ? selectedConversation.student_id 
          : selectedConversation.teacher_id
      });
      
      setNewMessage('');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/chat/${conv.id}`);
    fetchMessages(conv.id);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#163329' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }} data-testid="chat-page">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(user.role === 'teacher' ? '/teacher/dashboard' : '/student/dashboard')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            Messages
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-180px)]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden" style={{ borderColor: '#E3E1DB' }}>
            <div className="p-4 border-b" style={{ borderColor: '#E3E1DB' }}>
              <h2 className="font-semibold" style={{ color: '#163329' }}>Conversations</h2>
            </div>
            <ScrollArea className="h-[calc(100%-60px)]">
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>No conversations yet</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-gray-100' : ''
                    }`}
                    style={{ borderColor: '#E3E1DB' }}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 flex-shrink-0" style={{ color: '#163329' }} />
                          <p className="font-medium truncate" style={{ color: '#163329' }}>
                            {conv.other_user_name}
                          </p>
                          {conv.unread_count > 0 && (
                            <span 
                              className="px-2 py-0.5 text-xs rounded-full text-white"
                              style={{ backgroundColor: '#D86B47' }}
                            >
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm truncate" style={{ color: '#5C635A' }}>
                          {conv.course_title}
                        </p>
                        {conv.last_message && (
                          <p className="text-sm truncate mt-1" style={{ color: '#5C635A' }}>
                            {conv.last_message}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {conv.last_message_at && (
                          <span className="text-xs" style={{ color: '#5C635A' }}>
                            {formatTime(conv.last_message_at)}
                          </span>
                        )}
                        {!conv.chat_enabled && (
                          <MessageSquareOff className="h-4 w-4" style={{ color: '#D86B47' }} title="Chat disabled" />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </Card>

          {/* Messages Area */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden" style={{ borderColor: '#E3E1DB' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: '#E3E1DB' }}>
                  <div>
                    <h3 className="font-semibold" style={{ color: '#163329' }}>
                      {selectedConversation.other_user_name || 
                        (user.role === 'teacher' ? 'Student' : 'Teacher')}
                    </h3>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      {selectedConversation.course_title}
                    </p>
                  </div>
                  {!chatEnabled && (
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
                      <MessageSquareOff className="h-4 w-4" style={{ color: '#DC2626' }} />
                      <span className="text-sm" style={{ color: '#DC2626' }}>Chat disabled</span>
                    </div>
                  )}
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageCircle className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                        <p style={{ color: '#5C635A' }}>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] p-3 rounded-2xl ${
                              msg.sender_id === user.id
                                ? 'rounded-br-sm'
                                : 'rounded-bl-sm'
                            }`}
                            style={{
                              backgroundColor: msg.sender_id === user.id ? '#163329' : '#E3E1DB',
                              color: msg.sender_id === user.id ? '#FAF9F6' : '#163329'
                            }}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <Clock className="h-3 w-3 opacity-60" />
                              <span className="text-xs opacity-60">
                                {formatTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                  {chatEnabled ? (
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        disabled={isSending}
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={isSending || !newMessage.trim()}
                        className="rounded-full px-4"
                        style={{ backgroundColor: '#163329', color: '#FAF9F6' }}
                        data-testid="send-message-button"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-2 px-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                      <p className="text-sm" style={{ color: '#DC2626' }}>
                        Chat has been disabled by the teacher for this course.
                      </p>
                    </div>
                  )}
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
